class FaceSizePicker extends HTMLElement {
    constructor() {
        super();

        this.sectionId = this.dataset.sectionId;

        const pairedEl = this.querySelector('script[type="application/json"]#product_variants_pairs');
        const mostSoldEl = this.querySelector('script[type="application/json"]#most-sold-variant');
        const currentEl = this.querySelector('script[type="application/json"]#current_variant');

        this.rawPaired = pairedEl ? pairedEl.textContent.trim() : null;
        this.rawMostSold = mostSoldEl ? mostSoldEl.textContent.trim() : null;
        this.rawCurrent = currentEl ? currentEl.textContent.trim() : null;

        this.variants = this._parseVariants(this.rawPaired).filter(v => !v.vertex_clone);
        this.mostSoldVariant = this._parseMostSold(this.rawMostSold);
        const currentObj = this._parseCurrent(this.rawCurrent);

        this.currentVariantWidth = currentObj && currentObj.width != null ? Number(currentObj.width) : NaN;

        this.mostSuitableVariant = {};
        this.closestSuitableVariant = {};
        this.currentRange = {};
        this.alternativeText = '';
        this.sizeModalSpan = document.querySelector('[data-sizecontent]');
        this.sizeLabelElement = document.getElementById('size-label-js');

        this.ignoreUnavailability = this.dataset.ignoreUnavailability === 'true';
        this.isVariantInSelectedRange = true;
        this.selectedSizeWidthElement = this.querySelector('.selected-size-width');
        this.suitableLabel = this.querySelector('.js-suitable-label');
        this.buyBtnStatus = document.querySelectorAll('[data-btn-status]');

        this.processVariants();
    }

    _parseJSONSafe(raw) {
        if (!raw) return null;
        try {
            return JSON.parse(raw);
        } catch (e) {
            return null;
        }
    }

    _parseMostSold(raw) {
        const json = this._parseJSONSafe(raw);
        if (json && typeof json === 'object') return this._normalizeVariant(json);

        if (!raw) return {};
        const parts = raw.split('_');

        return this._normalizeVariant({
            id: parts[0],
            width: parts[1],
            model_size: parts[2],
            seg_height: parts[3],
            available: parts[4],
            temple_length: parts[5],
            frame_height: parts[6],
            disco: parts[7],
        });
    }

    _parseVariants(raw) {
        const json = this._parseJSONSafe(raw);
        if (Array.isArray(json)) return json.map(v => this._normalizeVariant(v));

        if (!raw) return [];

        const items = raw.split(';;').map(s => s.trim()).filter(Boolean);
        if (items.length) {
            return items.map(p => {
                const parts = p.split('_');
                return this._normalizeVariant({
                    id: parts[0],
                    width: parts[1],
                    model_size: parts[2],
                    seg_height: parts[3],
                    available: parts[4],
                    vertex_clone: parts[5],
                    temple_length: parts[6],
                    frame_height: parts[7],
                    disco: parts[8],
                });
            });
        }

        return [];
    }

    _parseCurrent(raw) {
        const json = this._parseJSONSafe(raw);
        if (json && typeof json === 'object') return this._normalizeVariant(json);

        if (!raw) return {};
        const n = parseInt(raw.replace(/[^0-9-]/g, ''), 10);
        return { width: isNaN(n) ? null : n };
    }

    _normalizeVariant(v) {
        if (!v) return v;
        return {
            id: v.id != null ? String(v.id) : undefined,
            width: v.width != null ? Number(v.width) : undefined,
            model_size: v.model_size != null ? String(v.model_size) : undefined,
            seg_height: v.seg_height != null ? String(v.seg_height) : undefined,
            available: this._parseBoolean(v.available),
            vertex_clone: this._parseBoolean(v.vertex_clone),
            temple_length: v.temple_length != null ? String(v.temple_length) : undefined,
            frame_height: v.frame_height != null ? String(v.frame_height) : undefined,
            disco: this._parseBoolean(v.disco),
            inventory_quantity: v.inventory_quantity == null || v.inventory_quantity === '' ? null : Number(v.inventory_quantity),
        };
    }

    _parseBoolean(val) {
        if (val === true || val === 'true' || val === 1 || val === '1') return true;
        if (val === false || val === 'false' || val === 0 || val === '0') return false;
        return Boolean(val);
    }

    get isCurrentRageWide() {
        return this.currentRange.key == FACE_SIZE_RANGE.wide.key;
    }

    get getAlternativeText() {
        let alternativeText = '';
        if (this.closestSuitableVariant) {
            this.closestSuitableVariant.width > this.mostSuitableVariant.width? alternativeText = window.variantStrings.wider : alternativeText = window.variantStrings.narrower;
        }
        return alternativeText;
    }

    get getSizeWarning() {
        let warning = '';
        switch (this.currentRange.key) {
            case FACE_SIZE_RANGE.narrow.key:
                warning = 'This size might be too wide for you';
                break;
            case FACE_SIZE_RANGE.wide.key:
                warning = 'This size might be too narrow for you';
                break;
            default:
                warning = '';
                break;
        }
        return warning;
    }

    /* ----------------------
    Lifecycle
    ---------------------- */
    connectedCallback() {
        const sizePickers = this.querySelectorAll('.face-size-input');
        sizePickers.forEach((sizePicker) => {
            sizePicker.addEventListener('click', (event) => {
                this.currentRange = FACE_SIZE_RANGE[`${event.target.value}`];

                if (this.currentRange.key == FACE_SIZE_RANGE.average.key) {
                    this.isVariantInSelectedRange = true;
                    this.hideSizeWarning();
                }
                this.calculateBestSize(event.target.value);
            });
        });

        const sizeLabels = this.querySelectorAll('.face-size-label');
        sizeLabels.forEach((label) => {
            label.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    const inputId = label.getAttribute('for');
                    const input = this.querySelector(`#${inputId}`);
                    if (input) {
                        input.click();
                    }
                }
            });
        });

        const confirmBtn = this.querySelector('button.size-confirm-btn');
        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => {
                this.confirmSuggestedVariant();
                this.changeSizeGuideOverlay();
                this.updateProductStatus();
            });
        }

        const mostSoldInput = this.querySelector('.most-sold-face-size-input');
        if (mostSoldInput) {
            mostSoldInput.addEventListener('click', () => {
                this.mostSuitableVariant = this.mostSoldVariant;
                this.updateSuggestedVariant();
            });
        }

        // currentVariant derivata dalle variants già parse-ate
        this.currentVariant = this.variants.find(variant => variant.width == this.currentVariantWidth);

        if (this.currentVariant) {
            this.mostSuitableVariant = this.currentVariant;
            this.updateProductStatus();
        }

        const usuallyPurchasedDiv = this.querySelector('.unique-size-container');
        if (usuallyPurchasedDiv) {
            usuallyPurchasedDiv.addEventListener('click', () => {
                const suggested = this.querySelector('.suggested-size-container');
                if (suggested) suggested.style = "display:none";
            });
        }
    }

    /* ----------------------
        processVariants: ora semplice (i dati sono già parse-ati)
        ---------------------- */
    processVariants() {
        // Imposta la most-sold come default

        if (this.mostSoldVariant && this.mostSoldVariant.id != null) {
            this.mostSuitableVariant = this.mostSoldVariant;
        } else if (this.variants && this.variants.length) {
            this.mostSuitableVariant = this.variants[0];
        } else {
            this.mostSuitableVariant = {};
        }
    }

    /* ----------------------
    Logica di selezione
    ---------------------- */
    calculateBestSize() {
        this.mostSuitableVariant.rangeEndDistance = 0;

        if (this.currentRange.key == FACE_SIZE_RANGE.average.key) {
            this.setClosestSuitableVariant();
        } else {
            this.setMostSuitableVariant();
        }

        this.updateSuggestedVariant();
    }

    setMostSuitableVariant() {
        let variantFound = false;
        this.variants.forEach((variant) => {
            if (variant.width >= this.currentRange.min && variant.width <= this.currentRange.max) {
                this.searchVariant(variant);
                variantFound = true;
                this.hideSizeWarning();
                this.isVariantInSelectedRange = true;
            }
        });

        if (!variantFound) {
            this.isVariantInSelectedRange = false;

            if (this.currentRange.key === FACE_SIZE_RANGE.wide.key) {
                this.mostSuitableVariant = this.variants.reduce((wider, v) =>
                    v.width > wider.width ? v : wider
                );
            } else {
                if (this.currentRange.key != FACE_SIZE_RANGE.average.key) {
                    this.displaySizeWarning(this.getSizeWarning);
                }
                this.currentRange = FACE_SIZE_RANGE.average;
                this.setClosestSuitableVariant();
            }
        }
    }

    searchVariant(variant, min = this.currentRange.min, max = this.currentRange.max, isRangeWide = this.isCurrentRageWide) {
        let distance = 0;
        distance = isRangeWide ? variant.width - min : Math.abs(max - variant.width);
        const shouldUpdate = this.checkNeededUpdate(distance);
        this.closestSuitableVariant = this.mostSuitableVariant;
        if (shouldUpdate) {
            this.closestSuitableVariant = this.mostSuitableVariant;
            this.mostSuitableVariant = variant;
            this.mostSuitableVariant.rangeEndDistance = distance;
        }
    }

    checkNeededUpdate(distance) {
        if (this.currentRange.key == FACE_SIZE_RANGE.average.key) {
            return distance < this.mostSuitableVariant.rangeEndDistance;
        }
        return distance >= (this.mostSuitableVariant.rangeEndDistance ?? 0);
    }

    setClosestSuitableVariant() {
        const midRange = parseInt((FACE_SIZE_RANGE.average.min + FACE_SIZE_RANGE.average.max) / 2);

        this.variants.forEach((variant, index) => {
            if (index == 0) {
                this.mostSuitableVariant = variant;
                this.mostSuitableVariant.rangeEndDistance = 100;
                this.closestSuitableVariant = variant;
            }

            this.searchVariant(variant, FACE_SIZE_RANGE.narrow.min, midRange);
        });
    }

    setClosestAsMostSuitable() {
        this.mostSuitableVariant = this.closestSuitableVariant;
        this.updateSuggestedVariant();
    }

    confirmSuggestedVariant() {
        const newUrl = window.location.pathname + `?variant=${this.mostSuitableVariant.id}`;
        // Check if the selected variant is different from the current one
        if (this.mostSuitableVariant.available != this.currentVariant?.available) {
           UrlUtils.addQueryParam('variant', this.mostSuitableVariant.id);
        }

        publish(PUB_SUB_EVENTS.faceSizeSelection, {
            data: {
                url: newUrl,
                variant: this.mostSuitableVariant,
                range: this.currentRange
            },
        });
    }

    changeSizeGuideOverlay(){
        document.querySelectorAll('[data-current-glasses-model-size]').forEach(el => {
            el.innerHTML = this.mostSuitableVariant.model_size;
        });

        document.querySelectorAll('[data-current-glasses-frame-width]').forEach(el => {
            el.innerHTML = this.mostSuitableVariant.width;
        });

        document.querySelectorAll('[data-current-glasses-width-size]').forEach(el => {
            el.innerHTML = this.mostSuitableVariant.seg_height;
        });

        document.querySelectorAll('[data-current-glasses-model-size-dinamic]').forEach(el => {
            const unit = el.getAttribute('unit');
            el.innerHTML = this.mostSuitableVariant.model_size+unit;
            el.setAttribute('data-value', this.mostSuitableVariant.model_size);
        });

        document.querySelectorAll('[data-current-glasses-bridge-width-dinamic]').forEach(el => {
            const unit = el.getAttribute('unit');
            el.innerHTML = this.mostSuitableVariant.seg_height+unit;
            el.setAttribute('data-value', this.mostSuitableVariant.seg_height);
        });


        document.querySelectorAll('[data-measurements-model-size-dinamic]').forEach(el => {
            const unit = el.getAttribute('unit');
            el.innerHTML = this.mostSuitableVariant.model_size+unit;
            el.setAttribute('data-value', this.mostSuitableVariant.model_size);
        });

        document.querySelectorAll('[data-measurements-bridge-width-dinamic]').forEach(el => {
            const unit = el.getAttribute('unit');
            el.innerHTML = this.mostSuitableVariant.seg_height+unit;
            el.setAttribute('data-value', this.mostSuitableVariant.seg_height);
        });

        document.querySelectorAll('[data-measurements-temple-length-dinamic]').forEach(el => {
            const unit = el.getAttribute('unit');
            el.innerHTML = this.mostSuitableVariant.temple_length+unit;
            el.setAttribute('data-value', this.mostSuitableVariant.temple_length);
        });

        document.querySelectorAll('[data-measurements-frame-height-dinamic]').forEach(el => {
            const unit = el.getAttribute('unit');
            el.innerHTML = this.mostSuitableVariant.frame_height+unit;
            el.setAttribute('data-value', this.mostSuitableVariant.frame_height);
        });

    }

    checkVariantsInCluster(range) {
        let find = [];
        this.variants.forEach(element => {
            if(element.width >= range.min && element.width <= range.max) {
                find.push(element);
            }
        })

        return find
    }

    getAlternativeVariant(current, allVariants) {
        let result = null
        allVariants.forEach(variant => {
            if(variant.width != current.width) {
                result = variant
            }
        })
        return result
    }

    displayAlternativeSize() {
        const currentRangeSelected = document.querySelector('.face-size-input:checked').value
        const currentRageValues = FACE_SIZE_RANGE[currentRangeSelected];
        const variantsInCluster = this.checkVariantsInCluster(currentRageValues)
        const alternativeVariant = this.getAlternativeVariant(this.mostSuitableVariant, variantsInCluster)
        const btn = this.querySelector('button.alternative-size-container');
        const span = this.querySelector('span.alternative-size');

        if (!alternativeVariant) {
            if (btn) btn.style = "display:none";
            return;
        }

        if (this.closestSuitableVariant.model_size && this.closestSuitableVariant.width != this.mostSuitableVariant.width && alternativeVariant.model_size != this.closestSuitableVariant.model_size && variantsInCluster.length > 1) {
            // Check if closestSuitableVariant is inside the current Range
            const inRange = this.closestSuitableVariant.width >= this.currentRange.min &&
                            this.closestSuitableVariant.width <= this.currentRange.max

            if (btn && span && currentRangeSelected == this.currentRange.key && inRange) {
                btn.style = "display:block";
                span.innerHTML = `${this.getAlternativeText} (${this.closestSuitableVariant.model_size}-${this.closestSuitableVariant.seg_height})`;
                btn.addEventListener('click', (event) => {
                    this.setClosestAsMostSuitable();
                });
            } else {
                const btn = this.querySelector('button.alternative-size-container');
                if (btn) btn.style = "display:none";
            }
        } else {
            const currentSize = alternativeVariant.model_size + '-' + alternativeVariant.seg_height
            if(variantsInCluster.length > 1 ) {
                btn.style = "display:block";
                const alternativeText = this.mostSuitableVariant.model_size < alternativeVariant.model_size ? window.variantStrings.wider : window.variantStrings.narrower;
                span.innerHTML = `${alternativeText} (${currentSize})`;
                btn.addEventListener('click', () => {
                    this.mostSuitableVariant = alternativeVariant
                    this.checkOutOfStock();
                    this.querySelector('.suggested-size-container').style = "display:flex";
                    this.selectedSizeWidthElement.innerHTML = `${this.mostSuitableVariant.model_size}-${this.mostSuitableVariant.seg_height}`;
                    // const btn = this.querySelector('button.alternative-size-container');
                    // if (btn) btn.style = "display:none";
                    this.displayAlternativeSize();
                });
            } else {
                // Check if
                const btn = this.querySelector('button.alternative-size-container');
                if (btn) btn.style = "display:none";
            }
        }
    }

    displaySizeWarning() {
        const optionSuitable = this.querySelector(".option-suitable")
        const optionSuitableOption = this.querySelector(".option-suitable-option")
        const warningText = this.getSizeWarning;
        if (!this.isVariantInSelectedRange) {
            const span = this.querySelector('span.size-warning-text');
            const container = this.querySelector('div.size-warning');
            if (span) span.innerHTML = warningText;
            if (container) container.style = "display:flex";

            optionSuitable?.toggleAttribute("data-hidden", true)
            optionSuitableOption?.toggleAttribute("data-hidden", false)
            // Hide alternative size
            const btn = this.querySelector('button.alternative-size-container');
                if (btn) btn.style = "display:none";
        }
    }

    hideSizeWarning() {
        const optionSuitable = this.querySelector(".option-suitable")
        const optionSuitableOption = this.querySelector(".option-suitable-option")
        const container = this.querySelector('div.size-warning');
        if (container) container.style = "display:none";
        optionSuitable?.toggleAttribute("data-hidden", false)
        optionSuitableOption?.toggleAttribute("data-hidden", true)
    }

    checkOutOfStock() {
        const isAvailable = !!this.mostSuitableVariant.available || this.ignoreUnavailability;
        const out_of_stock_container = this.querySelector('div.out-of-stock-banner-container');
        const sold_out_container = this.querySelector('.suggested-size-container .sold-out-banner-container');
        if (!out_of_stock_container && !sold_out_container) return;
        const soldOut = !isAvailable && this.mostSuitableVariant.disco === true;
        if (sold_out_container) sold_out_container.style.display = soldOut ? 'flex' : 'none';
        if (out_of_stock_container) out_of_stock_container.style.display = !isAvailable && !soldOut ? 'flex' : 'none';
        if(!isAvailable){
            this.selectedSizeWidthElement.classList.add('tw-line-through');
        }
        else{
            this.selectedSizeWidthElement.classList.remove('tw-line-through');
        }
    }

    updateSuggestedVariant() {
        this.checkOutOfStock();
        this.querySelector('.suggested-size-container').style = "display:flex";
        this.selectedSizeWidthElement.innerHTML = `${this.mostSuitableVariant.model_size}-${this.mostSuitableVariant.seg_height}`;
        this.displayAlternativeSize();
    }

    changeCaption() {
        switch (this.currentRange.key) {
            case FACE_SIZE_RANGE.narrow.key:
                return this.sizeLabelElement?.dataset.captionPetite;
            case FACE_SIZE_RANGE.average.key:
                return this.sizeLabelElement?.dataset.captionStandard;
            case FACE_SIZE_RANGE.wide.key:
                return this.sizeLabelElement?.dataset.captionGenerous;
            default:
                return this.sizeLabelElement?.dataset.captionStandard;
        }
    }

    updateProductStatus() {
        const productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
        if (!productInfo) return;

        /* on page load */
        const productAvailable = String(productInfo.dataset.productAvailable).toLowerCase() === 'true';
        const disco = String(productInfo.dataset.disco).toLowerCase() === 'true';
        let variantAvailable = !!this.mostSuitableVariant?.available;

        /* check after update */
        const discoExist = String(productInfo.dataset.discoExist).toLowerCase() === 'true';
        const discoVariantId = String(productInfo.dataset.variantDiscoId);
        const variantId = this.mostSuitableVariant.id;

        if (discoExist && discoVariantId.includes(variantId) && !variantAvailable){
            variantAvailable = false;
        }
        let status = 'product_ok';
        if ((discoExist || disco)) {
            if (productAvailable && !variantAvailable) {
                status = 'sold_out';
            } else if (!productAvailable){
            status = 'not_available_redirect_collection';
            }
        } else if (!variantAvailable) {
            status = 'not_available_notify_me';
        }

        productInfo.setAttribute('data-status', status);

        this.buyBtnStatus?.forEach((el )=>{
            el.setAttribute('data-btn-status', status);
        })
    }

}

if (!customElements.get('face-size-picker')) {
    customElements.define('face-size-picker', FaceSizePicker);
}
